// Persistent per-origin user store. Holds the values the beacon has observed
// for the current visitor (visitorId, page_url, fbp/fbc, ip, UA, sanitized
// PII). Mirrored to a cookie + localStorage so values survive page nav and
// can be referenced later in the same session.
//
// Note: stored PII is already Meta-normalized — same hashing input on both
// sides, so the browser pixel and the server CAPI produce identical hashes.

import { readCookie, writeCookie } from './cookie';

export interface UserStore {
  // identity / network signals
  uuid?: string;          // FingerprintJS visitorId
  page_url?: string;      // current page URL at time of capture
  _fbp?: string;
  _fbc?: string;
  ip?: string;
  user_agent?: string;

  // PII — already Meta-normalized (lowercased, trimmed, etc.)
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;

  updated_at?: number;
}

const COOKIE_KEY = 'capi_store';
const LS_KEY = 'capi_store';
const COOKIE_TTL_SECONDS = 365 * 24 * 60 * 60;

let inMemory: UserStore = {};

export function loadStore(): UserStore {
  try {
    const ls = localStorage.getItem(LS_KEY);
    if (ls) { inMemory = JSON.parse(ls) as UserStore; return inMemory; }
  } catch { /* private mode */ }

  const cookie = readCookie(COOKIE_KEY);
  if (cookie) {
    try { inMemory = JSON.parse(cookie) as UserStore; return inMemory; }
    catch { /* malformed */ }
  }
  return inMemory;
}

export function getStore(): UserStore {
  return { ...inMemory };
}

export function updateStore(partial: Partial<UserStore>): UserStore {
  const cleaned = stripBlanks(partial);
  if (!Object.keys(cleaned).length) return inMemory;

  inMemory = { ...inMemory, ...cleaned, updated_at: Date.now() };
  persist(inMemory);
  return inMemory;
}

// Like updateStore, but only sets keys that are currently empty/missing.
// Used for "first-touch" values (_fbp/_fbc) that must persist through the
// funnel even if Meta's pixel sets new per-domain values later.
export function updateStoreSticky(partial: Partial<UserStore>): UserStore {
  const cleaned = stripBlanks(partial);
  let changed = false;
  const next = { ...inMemory } as Record<string, unknown>;
  for (const [k, v] of Object.entries(cleaned)) {
    const existing = next[k];
    if (existing === undefined || existing === null || existing === '') {
      next[k] = v;
      changed = true;
    }
  }
  if (!changed) return inMemory;
  next.updated_at = Date.now();
  inMemory = next as UserStore;
  persist(inMemory);
  return inMemory;
}

// Wipe a key from the store (used when garlic.js restored stale CF custom
// fields and we don't have a current value to replace it with).
export function clearStoreKey(key: keyof UserStore): UserStore {
  if (!(key in inMemory)) return inMemory;
  const next = { ...inMemory };
  delete next[key];
  next.updated_at = Date.now();
  inMemory = next;
  persist(inMemory);
  return inMemory;
}

function stripBlanks(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && !v.trim()) continue;
    out[k] = v;
  }
  return out;
}

function persist(store: UserStore): void {
  const json = JSON.stringify(store);
  try { localStorage.setItem(LS_KEY, json); } catch { /* private mode */ }
  writeCookie(COOKIE_KEY, json, COOKIE_TTL_SECONDS);
}
